import cx from 'classnames';
import { LocationDescriptor } from 'history';
import Link from 'mk2/components/Link';
import LoadingPlaceholder from 'mk2/components/LoadingPlaceholder';
import NotificationsBadge, { Count } from 'mk2/components/NotificationsBadge';
import React from 'react';
import styles from './ListItem.mscss';

interface OwnProps {
    /** Custom style properties */
    style?: React.CSSProperties;

    /** Add custom classname to container */
    className?: string;

    /** Destination for the react router <Link> */
    to?: LocationDescriptor;

    /** Custom icon for the item */
    icon?: React.ReactNode;

    /** If true it will display right chevron icon on the right */
    hasMore?: boolean;

    /** If true bottom border will not be displayed */
    isLast?: boolean;

    /** Optional element displayed before the count number */
    rightElement?: React.ReactNode;

    /** Number displayed as counter */
    count?: Count;

    /** Display count as badge */
    asBadge?: boolean;

    /** Click handler for the component */
    onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>;
}

export class ListItem extends React.Component<OwnProps> {
    public shouldComponentUpdate(n: OwnProps & { children?: React.ReactNode }) {
        const p = this.props;

        return (
            p.isLast !== n.isLast || p.children !== n.children // isLast can be changed when loading
        ); // Only children has changed
    }

    public render() {
        const {
            className,
            to,
            style,
            icon,
            children,
            hasMore,
            isLast,
            count,
            onClick,
            asBadge,
            rightElement,
        } = this.props;

        const box = (
            <div className={styles.ListItem__box}>
                {icon && <div className={styles.ListItem__icon}>{icon}</div>}
                <div className={styles.ListItem__content}>{children}</div>
                {rightElement && <div className={styles.ListItem__rightElement}>{rightElement}</div>}
                {count !== undefined && (
                    <div
                        className={cx(
                            styles.ListItem__count,
                            asBadge && styles['ListItem__count--asBadge'],
                        )}
                    >
                        {asBadge ? <NotificationsBadge count={count} /> : count}
                    </div>
                )}
                {hasMore && (
                    <div className={styles.ListItem__hasMore}>
                        <i className="fa fa-angle-right" />
                    </div>
                )}
            </div>
        );

        if (to) {
            return (
                <Link
                    className={cx(styles.ListItem, isLast && styles['ListItem--last'], className)}
                    style={style}
                    to={to}
                    onClick={onClick}
                >
                    {box}
                </Link>
            );
        } else {
            return (
                <div
                    className={cx(styles.ListItem, isLast && styles['ListItem--last'], className)}
                    style={style}
                    onClick={onClick}
                >
                    {box}
                </div>
            );
        }
    }
}

export default ListItem;

interface PlaceholderOwnProps {
    /** Custom CSS style properties */
    style?: React.CSSProperties;

    /** If true it will display icon placeholder */
    icon?: boolean;

    /** If true bottom border will not be displayed */
    isLast?: boolean;
}

export const ListItemPlaceholder: React.StatelessComponent<PlaceholderOwnProps> = ({
    style,
    icon,
    isLast,
}) => (
    <div className={cx(styles.ListItem, isLast && styles['ListItem--last'])} style={style}>
        <div className={styles.ListItem__box}>
            {icon && (
                <div className={styles.ListItem__icon}>
                    <LoadingPlaceholder width={32} height={32} style={{ borderRadius: '4px' }} />
                </div>
            )}
            <div className={styles.ListItem__content}>
                <LoadingPlaceholder width="100%" height={14} />
            </div>
        </div>
    </div>
);
ListItemPlaceholder.displayName = 'ListItemPlaceholder';
